import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalUrl, canonicalLink, geoMeta, keywordsWithLocal } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { ChevronDown, ChevronRight, Home } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { WagonCoverForm } from "@/components/WagonCoverForm";
import { DropCurtainForm } from "@/components/DropCurtainForm";
import { BoatCoverForm } from "@/components/BoatCoverForm";
import { CoolerCushionForm } from "@/components/CoolerCushionForm";
import { HuntingTarpForm } from "@/components/HuntingTarpForm";
import { StockTrailerForm } from "@/components/StockTrailerForm";
import { RvQuoteForm } from "@/components/RvQuoteForm";
import { GazeboCoverForm } from "@/components/GazeboCoverForm";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { DogBoxQuoteForm } from "@/components/DogBoxQuoteForm";
import bannerImg from "@/assets/awning-bradzoil-banner.jpg";
import wagonImg from "@/assets/historic-covered-wagon-bow-frame-texas-pasture.jpg";
import dropCurtainImg from "@/assets/clear-vinyl-drop-curtains-pavilion-enclosure.jpg";
import boatCoverImg from "@/assets/boat-cover-black-lakehouse.jpg";
import rvImg from "@/assets/rv-retractable-awning.jpg";
import gazeboImg from "@/assets/pergola-blue-canopy-poolside-twilight.jpg";
import stockTrailerImg from "@/assets/blue-stock-trailer-tan-tarp.jpg";
import huntingImg from "@/assets/hunting-gallery-deer-blind.jpg";
import coolerImg from "@/assets/cooler-cushion-rtic.png";
import dogBoxImg from "@/assets/custom-welded-aluminum-dog-box-hunting-truck-seguin-tx.jpg";
import generalImg from "@/assets/original/hero-shade.jpg";

export const Route = createFileRoute("/estimate-quotes")({
  head: () => ({
    links: [canonicalLink("/estimate-quotes")],
    meta: [
        ...geoMeta(),{ property: "og:url", content: canonicalUrl("/estimate-quotes") },
      { title: "Free Quote Forms — Awnings, Covers & Shade in Seguin, San Antonio, Austin TX" },
      {
        name: "description",
        content:
          "Free estimates on custom awnings, patio & pergola covers, sail shades, boat & RV covers, drop curtains and trailer tarps — Seguin, San Antonio & Austin, TX.",
      },
      {
        name: "keywords",
        content: keywordsWithLocal(
          "awning quote Texas, free estimate awnings Seguin, custom awning San Antonio, patio cover quote Austin, pergola cover New Braunfels, boat cover quote Corpus Christi, RV cover Texas, drop curtain quote, stock trailer tarp Texas, sail shade estimate, Hill Country shade quote",
        ),
      },
      { property: "og:title", content: "Free Quote Forms — Texas Made Shade | Seguin, San Antonio, Austin" },
      {
        property: "og:description",
        content:
          "All quote forms in one place — awnings, covers, sail shades, curtains, boat, RV, trailer & more. Serving Seguin, San Antonio, New Braunfels, Austin & South Texas.",
      },
    ],
    scripts: pageHeadScripts({
      path: "/estimate-quotes",
      name: "Free Quote Forms — Texas Made Shade",
      description: "Request a free estimate for custom awnings, patio & pergola covers, sail shades, boat & RV covers, drop curtains, stock trailer tarps and more across Texas.",
      faqs: [
        { q: "How fast will I hear back after I submit a quote form?", a: "We reply same business day from a real person in Seguin, Texas — typically with sizing/photo questions and a written estimate within 24–48 hours." },
        { q: "Do you serve San Antonio, Austin and Corpus Christi?", a: "Yes. We build in Seguin and install across San Antonio, Austin, New Braunfels, Corpus Christi, the Hill Country and the entire Gulf Coast." },
        { q: "Is the estimate really free?", a: "Yes — every quote is free with no obligation. On-site measurements are available across our Texas service area." },
      ],
    }),
  }),
  component: EstimateQuotesPage,
});

const QUOTE_OPTIONS = [
  { id: "wagon-cover", label: "Wagon Cover" },
  { id: "drop-curtain", label: "Drop Curtain" },
  { id: "boat-cover", label: "Boat Cover" },
  { id: "rv-cover", label: "RV Cover" },
  { id: "gazebo-cover", label: "Gazebo / Pergola Cover" },
  { id: "stock-trailer", label: "Stock Trailer Cover" },
  { id: "hunting-tarp", label: "Hunting Tarp / Blind Cover" },
  { id: "cooler-cushion", label: "Cooler Cushion / Gear Cover" },
  { id: "dog-box", label: "Dog Box / Hunting Truck" },
  { id: "general", label: "General / Other Project" },
] as const;

function scrollToId(id: string) {
  // Tell the target section to open (mobile collapsed state)
  window.dispatchEvent(new CustomEvent("estimate-quotes:open-section", { detail: id }));
  // Wait a tick so the section expands before scrolling
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function EstimateQuotesPage() {
  return (
    <>
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
      {/* BREADCRUMBS */}
      <nav aria-label="Breadcrumb" className="border-b border-navy-deep/10 bg-cream">
        <ol className="mx-auto flex max-w-5xl flex-wrap items-center gap-1.5 px-4 py-3 font-display text-xs uppercase tracking-wider text-navy-deep/70 sm:text-sm">
          <li className="inline-flex items-center gap-1.5">
            <Link to="/" className="inline-flex items-center gap-1 text-navy-deep transition hover:text-red focus:outline-none focus-visible:underline">
              <Home className="h-3.5 w-3.5" aria-hidden="true" focusable="false" />
              Home
            </Link>
          </li>
          <li aria-hidden="true" className="text-navy-deep/70">
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="font-bold text-navy-deep">
            Estimate Quotes
          </li>
        </ol>
      </nav>
      {/* BANNER */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0">
          <img
            src={bannerImg}
            alt="Texas Made Shade custom awnings, covers and shade structures"
            className="h-full w-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/85 via-navy-deep/60 to-navy-deep/30" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-16 text-center sm:py-24">
          <p className="font-display text-xs uppercase tracking-[0.3em] text-white/80 sm:text-sm">
            ★ Texas Made Shade ★
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl md:text-6xl">
            Free Quote Forms — Awnings, Covers & Shade in Seguin, San Antonio & Austin, TX
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/90 sm:text-lg">
            Pick what you need a quote for — custom awnings, patio & pergola covers, sail shades, boat & RV covers, drop curtains, stock trailer tarps and more. Serving Seguin, San Antonio, New Braunfels, Austin, Corpus Christi, the Hill Country and all of South Texas.
          </p>
        </div>
        <div className="stripe-bar absolute bottom-0 left-0 h-2 w-full" />
      </section>

      <section className="sticky top-0 z-30 border-b-4 border-navy-deep bg-white py-4 shadow-md md:static md:py-10 md:shadow-none lg:py-12">
        <div className="mx-auto max-w-xl px-4">
          <label htmlFor="form-nav" className="mb-2 block text-center font-display text-sm uppercase tracking-wide text-navy-deep/70">
            Jump to a quote form
          </label>
          <select
            id="form-nav"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) scrollToId(e.target.value);
            }}
            className="w-full rounded-none border-2 border-navy-deep bg-cream px-3 py-2 font-display text-sm uppercase tracking-wide text-navy-deep shadow-card focus:outline-none focus:ring-2 focus:ring-red"
          >
            <option value="">Select form type…</option>
            {QUOTE_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <Section id="wagon-cover" eyebrow="01" title="Wagon Cover Quote" image={wagonImg} imageAlt="Historic covered wagon with bow frame in Texas pasture">
        <WagonCoverForm />
      </Section>
      <Section id="drop-curtain" eyebrow="02" title="Drop Curtain Quote" image={dropCurtainImg} imageAlt="Clear vinyl drop curtains enclosing a pavilion">
        <DropCurtainForm />
      </Section>
      <Section id="boat-cover" eyebrow="03" title="Boat Cover Quote" image={boatCoverImg} imageAlt="Black custom boat cover at a lakehouse">
        <BoatCoverForm />
      </Section>
      <Section id="rv-cover" eyebrow="04" title="RV Cover Quote" image={rvImg} imageAlt="RV retractable awning shade">
        <RvQuoteForm />
      </Section>
      <Section id="gazebo-cover" eyebrow="05" title="Gazebo / Pergola Cover Quote" image={gazeboImg} imageAlt="Blue pergola canopy poolside at twilight">
        <GazeboCoverForm />
      </Section>
      <Section id="stock-trailer" eyebrow="06" title="Stock Trailer Cover Quote" image={stockTrailerImg} imageAlt="Blue stock trailer with tan custom tarp">
        <StockTrailerForm />
      </Section>
      <Section id="hunting-tarp" eyebrow="07" title="Hunting Tarp / Blind Cover Quote" image={huntingImg} imageAlt="Custom deer blind cover">
        <HuntingTarpForm />
      </Section>
      <Section id="cooler-cushion" eyebrow="08" title="Cooler Cushion / Gear Cover Quote" image={coolerImg} imageAlt="Custom RTIC cooler cushion">
        <CoolerCushionForm />
      </Section>
      <Section id="dog-box" eyebrow="09" title="Dog Box / Hunting Truck Quote" image={dogBoxImg} imageAlt="Custom welded aluminum dog box hunting truck built in Seguin, TX">
        <DogBoxQuoteForm />
      </Section>
      <Section id="general" eyebrow="10" title="General Project Inquiry" image={generalImg} imageAlt="Custom Texas Made Shade structure" last>
        <LetsTalkForm />
      </Section>
    </main>
    <SiteFooter />
    </>
  );
}

function Section({
  id,
  eyebrow,
  title,
  image,
  imageAlt,
  children,
  last,
}: {
  id: string;
  eyebrow: string;
  title: string;
  image: string;
  imageAlt: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  // Default: open on desktop (>=768px), collapsed on mobile.
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  });

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail === id) setOpen(true);
    };
    window.addEventListener("estimate-quotes:open-section", onOpen);
    // Open via direct anchor hash (page load or back/forward)
    if (typeof window !== "undefined" && window.location.hash === `#${id}`) {
      setOpen(true);
    }
    return () => window.removeEventListener("estimate-quotes:open-section", onOpen);
  }, [id]);

  return (
    <section
      id={id}
      className={`scroll-mt-24 ${last ? "" : "border-b-2 border-navy-deep/20"}`}
    >
      <div className="mx-auto max-w-6xl px-4 pt-6 sm:pt-12 md:pt-16">
        <div className="group flex w-full items-start justify-between gap-3 text-left">
          <div className="min-w-0">
            <p className="font-display text-[11px] uppercase tracking-[0.3em] text-navy-deep/60 sm:text-xs">
              Section {eyebrow}
            </p>
            <h2 className="mt-1 font-display text-2xl uppercase leading-tight text-navy-deep sm:text-3xl md:text-4xl">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            aria-label={`${open ? "Hide" : "Show"} ${title} section`}
            className="mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center text-navy-deep md:hidden"
          >
            <ChevronDown
              aria-hidden="true"
              className={`h-6 w-6 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        <div
          id={`${id}-panel`}
          hidden={!open}
          className="pb-8 sm:pb-12 md:!block"
        >
          <div className="mt-4 overflow-hidden border-2 border-navy-deep/20 bg-navy-deep sm:mt-6">
            <img
              src={image}
              alt={imageAlt}
              loading="lazy"
              decoding="async"
              className="h-40 w-full object-cover sm:h-72 md:h-80"
            />
          </div>
          <div className="mt-4 sm:mt-6">{children}</div>
        </div>
      </div>
    </section>
  );
}
